import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { OrderCard, type Order } from '../../../components/fila/order-card';
import { CompactOrderGrid } from '../../../components/fila/compact-order-grid';
import { ToggleSwitch } from '../../../components/shared/toggle-switch';
import { TextSizeControl } from '../../../components/shared/text-size-control';
import { Badge } from '@ui/badge';
import { ListTodo, Clock, CheckCircle, Wifi, WifiOff, RefreshCw, Calendar, Hash, AlertCircle } from 'lucide-react';
import { useOrdersByConfigType } from '../../../hooks/useConfiguredOrders';
import { useConfiguredWebSocket } from '../../../hooks/useConfiguredWebSocket';
import { useFilaConfig } from '../../../hooks/useFilaConfig';
import { useIntervalManager } from '../../../hooks/useIntervalManager';
import { useCurrentIntervalName } from '../../../hooks/useCurrentIntervalName';
import { useUserInfo } from '@queries/auth';
import { toast } from 'sonner';
import { getCurrentEstablishment } from '@utils/session';
import type { Interval } from '@services/api/orderTypes';
import { useFilaRefresh } from '../../../context/fila-refresh-context';
import { useQueryClient } from '@tanstack/react-query';
import { ORDER_KEYS } from '@queries/keys';
import { wsLog } from '@utils/wsLogger';

export const Route = createFileRoute('/(private)/fila/')({
  component: FilaManagement,
});

/**
 * Pedidos já reconciliados (HTTP + sobreposição do WebSocket), no formato que
 * a tela consome.
 *
 * `summary` aqui é a contagem por coluna da fila — não confundir com o
 * `summary` de TodayOrdersResponse, que é o resumo da API
 * ({total_orders, by_status, total_revenue}).
 */
interface FilaOrdersView {
  orders: any[];
  total: number;
  summary: { fila: number; preparando: number; pronto: number };
}

function FilaManagement() {
  const {
    config,
    syncEstablishmentId,
    setCurrentInterval: setConfigCurrentInterval,
    setOrderNumberTextSize,
    setQueueTitleTextSize,
  } = useFilaConfig();
  const currentIntervalName = useCurrentIntervalName();
  const { data: userInfo } = useUserInfo();
  const { registerRefreshCallback, registerWebSocketCallback } = useFilaRefresh();
  const queryClient = useQueryClient();
  const [expandedCards, setExpandedCards] = useState(false);
  const [lastApiSuccess, setLastApiSuccess] = useState<Date | null>(null);

  // Estado para trigger de recarga de pedidos
  const [ordersRefreshKey, setOrdersRefreshKey] = useState(0);

  // Ref para armazenar a função refetch de forma segura
  const refetchRef = useRef<(() => void) | null>(null);
  const reconnectRef = useRef<(() => void) | null>(null);

  // Priorizar intervalo da configuração (única fonte de verdade)
  // Em modo automático, usar intervalo do gerenciador; em modo manual, usar da configuração
  const currentInterval = config.currentInterval;

  // Obter establishmentId na ordem correta de prioridade:
  // 1. current_establishment do localStorage (padrão centralizado)
  // 2. config.establishmentId da configuração da fila
  // 3. activeProfile?.establishment_id do perfil do usuário
  const currentEstablishment = getCurrentEstablishment();
  // activeProfile?.establishment_id saiu daqui: /auth/userinfo devolve
  // ProfileLite {id, name, channel, active} — o campo nunca existiu na resposta.
  const establishmentId = currentEstablishment?.id || config.establishmentId || '';

  // Rastrear mudanças no establishmentId para forçar refresh quando trocar
  const previousEstablishmentIdRef = useRef<string>('');
  const isProcessingEstablishmentChangeRef = useRef<boolean>(false);
  const lastIntervalErrorTimeRef = useRef<number>(0);
  
  // Armazenar função em ref para evitar dependências
  const setConfigCurrentIntervalRef = useRef(setConfigCurrentInterval);
  useEffect(() => {
    setConfigCurrentIntervalRef.current = setConfigCurrentInterval;
  }, [setConfigCurrentInterval]);
  
  useEffect(() => {
    const previousId = previousEstablishmentIdRef.current;
    const currentId = establishmentId;
    
    // Se o establishmentId mudou e não é a primeira renderização
    // E não estamos processando uma mudança já
    if (previousId && currentId && previousId !== currentId && !isProcessingEstablishmentChangeRef.current) {
      isProcessingEstablishmentChangeRef.current = true;
      
      wsLog.debug('🔄 Estabelecimento mudou - forçando refresh completo:', {
        previousId,
        currentId,
        timestamp: new Date().toISOString(),
      });
      
      // Usar setTimeout para evitar atualizações síncronas que causam loops
      setTimeout(() => {
        try {
          // Invalidar todas as queries de pedidos para forçar nova busca
          queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.LIST_ORDERS] });
          queryClient.invalidateQueries({ queryKey: [ORDER_KEYS.GET_ORDER_STATISTICS] });
          queryClient.removeQueries({ queryKey: ['websocket-orders'] });
          
          // Limpar configuração de intervalo antiga ANTES de invalidar queries de intervalos
          // Isso evita que o useIntervalManager tente usar dados antigos
          setConfigCurrentIntervalRef.current(null);
          
          // Invalidar queries de intervalos DEPOIS de limpar a configuração
          // Usar um pequeno delay para garantir que a limpeza foi processada
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['establishment:intervals'] });
          }, 100);
          
          // Forçar refresh dos pedidos
          if (refetchRef.current) {
            refetchRef.current();
          }
          
          // Forçar reconexão do WebSocket
          if (reconnectRef.current) {
            reconnectRef.current();
          }
          
          // Atualizar refresh key para forçar nova busca
          setOrdersRefreshKey((prev) => prev + 1);
        } catch (error) {
          wsLog.error('❌ Erro ao processar mudança de estabelecimento:', error);
        } finally {
          // Resetar flag após processamento
          setTimeout(() => {
            isProcessingEstablishmentChangeRef.current = false;
          }, 2000);
        }
      }, 100);
    }
    
    // Atualizar referência
    previousEstablishmentIdRef.current = currentId;
  }, [establishmentId]); // Apenas establishmentId como dependência

  // Sincronizar establishmentId quando estiver disponível (com prevenção de loop)
  const syncEstablishmentIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (establishmentId && !config.establishmentId && establishmentId !== syncEstablishmentIdRef.current) {
      syncEstablishmentIdRef.current = establishmentId;
      syncEstablishmentId(establishmentId);
    }
  }, [establishmentId, config.establishmentId]); // Removido syncEstablishmentId das dependências

  // Debug do estado do gerenciador de intervalos
  wsLog.debug('🔧 Interval Manager Debug:', {
    establishmentId,
    autoIntervalChange: config.autoIntervalChange,
    enabled: !!establishmentId && config.autoIntervalChange === true,
    hasCurrentInterval: !!config.currentInterval,
    timestamp: new Date().toISOString(),
  });

  // Gerenciador de intervalos automáticos
  const {
    currentInterval: intervalFromManager,
    isChecking: isCheckingInterval,
    error: intervalError,
    lastChecked,
    forceCheck: forceIntervalCheck,
  } = useIntervalManager({
    establishmentId,
    enabled: !!establishmentId && config.autoIntervalChange === true, // Apenas no modo automático
    onIntervalChange: (newInterval, oldInterval) => {
      wsLog.debug('🔄 Auto interval change detected:', {
        establishmentId,
        newIntervalId: newInterval.id,
        newIntervalName: newInterval.name,
        oldIntervalId: oldInterval?.id,
        timestamp: new Date().toISOString(),
      });

      // Salvar intervalo completo na configuração (única fonte de verdade)
      setConfigCurrentInterval({
        id: newInterval.id,
        name: newInterval.name,
        startTime: newInterval.start_time,
        endTime: newInterval.end_time,
        isActive: newInterval.is_active,
        establishmentId: newInterval.establishment_id,
        orderCount: newInterval.order_count,
      });

      // Atualizar timestamp de sucesso da API
      setLastApiSuccess(new Date());

      // Forçar recarga dos pedidos com novo intervalo
      setOrdersRefreshKey((prev) => prev + 1);
    },
    onError: (error) => {
      wsLog.error('❌ Interval manager error:', error);
      // Usar ref para evitar múltiplos toasts em loop
      const now = Date.now();
      // Só mostrar toast se passou mais de 5 segundos desde o último
      if (now - lastIntervalErrorTimeRef.current > 5000) {
        lastIntervalErrorTimeRef.current = now;
        toast.error('Erro na verificação automática de intervalos', {
          description: 'A verificação automática está temporariamente indisponível.',
          duration: 5000,
        });
      }
    },
  });

  // Disparar refresh dos pedidos quando o intervalo mudar (com debounce)
  useEffect(() => {
    if (currentInterval && establishmentId) {
      wsLog.debug('🔄 Triggering orders refresh for interval change:', {
        intervalId: currentInterval.id,
        intervalName: currentInterval.name,
        establishmentId,
        orderType: config.orderType,
        timestamp: new Date().toISOString(),
      });

      // Adicionar pequeno delay para agrupar múltiplas mudanças rápidas
      const refreshTimer = setTimeout(() => {
        setOrdersRefreshKey((prev) => prev + 1);

        // Forçar nova request HTTP com os parâmetros atualizados
        // Usar ref armazenado para evitar erro de inicialização
        if (refetchRef.current) {
          wsLog.debug('🚀 Forcing new HTTP request with updated parameters');
          refetchRef.current();
        }
      }, 200);

      return () => clearTimeout(refreshTimer);
    }
  }, [currentInterval?.id, establishmentId, config.orderType]); // Dependências minimizadas para prevenir loops

  // Efeito adicional para detectar mudanças importantes na configuração (com debounce)
  const configChangeRef = useRef<string>('');
  useEffect(() => {
    const configSignature = JSON.stringify({
      autoIntervalChange: config.autoIntervalChange,
      selectedInterval: config.selectedInterval,
      websocketUpdateMode: config.websocketUpdateMode,
    });

    if (configChangeRef.current && configChangeRef.current !== configSignature) {
      wsLog.debug('🔄 Important configuration change detected:', {
        oldSignature: configChangeRef.current,
        newSignature: configSignature,
        timestamp: new Date().toISOString(),
      });

      // Adicionar debounce para evitar múltiplos refreshes rápidos
      const configRefreshTimer = setTimeout(() => {
        if (refetchRef.current) {
          wsLog.debug('🚀 Refreshing due to configuration change');
          refetchRef.current();
        }
      }, 300);

      return () => clearTimeout(configRefreshTimer);
    }

    configChangeRef.current = configSignature;
  }, [config.autoIntervalChange, config.selectedInterval, config.websocketUpdateMode]); // Dependências específicas

  // Carga inicial de pedidos usando configurações do localStorage
  // Adicionar chave de refresh para forçar novas requests quando mudar intervalo/filtros
  const {
    data: initialOrdersData,
    isLoading: isInitialLoading,
    error: initialError,
    params: apiParams,
    isConfigValid,
    refetch: refetchInitialOrders,
  } = useOrdersByConfigType(currentInterval?.id);

  // Atualizar ref sempre que a função refetch mudar (com debounce)
  const refetchUpdateTimer = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (typeof refetchInitialOrders === 'function') {
      // Debounce para evitar atualizações rápidas do ref
      if (refetchUpdateTimer.current) {
        clearTimeout(refetchUpdateTimer.current);
      }
      refetchUpdateTimer.current = setTimeout(() => {
        refetchRef.current = refetchInitialOrders;
        wsLog.debug('🔄 Refetch function updated');
      }, 100);
    }

    return () => {
      if (refetchUpdateTimer.current) {
        clearTimeout(refetchUpdateTimer.current);
      }
    };
  }, [refetchInitialOrders]);

  // Registrar callbacks no contexto quando estiverem disponíveis
  useEffect(() => {
    if (refetchRef.current) {
      registerRefreshCallback(() => {
        wsLog.debug('🔄 FilaManagement: Refresh callback triggered');
        refetchRef.current?.();
      });
    }
  }, [registerRefreshCallback]);

  // Conexão WebSocket para atualizações em tempo real usando configurações
  const {
    orders: websocketOrders,
    isLoading: isWebSocketLoading,
    error: webSocketError,
    connectionStatus,
    isConnected,
    lastUpdated,
    reconnect,
    webSocketKey,
    apiChannel,
  } = useConfiguredWebSocket({
    currentIntervalId: currentInterval?.id,
    enabled: !!establishmentId,
  });

  // Atualizar ref da função de reconexão do WebSocket
  useEffect(() => {
    if (typeof reconnect === 'function') {
      reconnectRef.current = reconnect;
    }
  }, [reconnect]);

  // Registrar callback do WebSocket no contexto
  useEffect(() => {
    if (reconnectRef.current) {
      registerWebSocketCallback(() => {
        wsLog.debug('🔄 FilaManagement: WebSocket reconnect callback triggered');
        reconnectRef.current?.();
      });
    }
  }, [registerWebSocketCallback]);

  // Forçar reconexão do WebSocket quando o establishmentId ou intervalo mudar significativamente
  const reconnectTriggerRef = useRef<string | null>(null);
  const lastReconnectTimeRef = useRef<number>(0);

  useEffect(() => {
    if (!isConnected || !currentInterval || !establishmentId) return;

    const triggerKey = `${establishmentId}-${currentInterval.id}`;
    const now = Date.now();

    // Evitar reconexões duplicadas ou muito frequentes (mínimo 5 segundos entre tentativas)
    if (reconnectTriggerRef.current === triggerKey || now - lastReconnectTimeRef.current < 5000) {
      return;
    }

    wsLog.debug('🔄 WebSocket reconnect triggered by interval/establishment change:', {
      establishmentId,
      newIntervalId: currentInterval.id,
      newIntervalName: currentInterval.name,
      connectionStatus,
      webSocketKey,
      timestamp: new Date().toISOString(),
    });

    reconnectTriggerRef.current = triggerKey;
    lastReconnectTimeRef.current = now;

    // Desconectar e reconectar o WebSocket para usar o novo intervalo
    const reconnectTimer = setTimeout(() => {
      if (reconnectTriggerRef.current === triggerKey) {
        wsLog.debug('🔄 Executing scheduled WebSocket reconnect:', {
          establishmentId,
          intervalId: currentInterval.id,
          timestamp: new Date().toISOString(),
        });
        reconnect();
      }
    }, 1000);

    return () => clearTimeout(reconnectTimer);
  }, [isConnected, currentInterval?.id, establishmentId, reconnect]); // Apenas dependências essenciais

  // Função de validação de dados de pedidos (memoizada e otimizada)
  // student_name e user_name não são obrigatórios - podem ser exibidos sem nome
  const validateOrderData = useCallback((order: any, source: string) => {
    if (__DEBUG_MODE__) {
      wsLog.debug('🔍 DEBUG validateOrderData:', {
        source,
        orderId: order.id,
        hasRequiredFields: !!(order.id && order.order_number && order.status && order.created_at),
        hasStudentName: !!order.student_name,
        hasUserName: !!order.user_name,
      });
    }

    // Campos realmente obrigatórios (sem esses não dá para exibir o pedido)
    const requiredFields = ['id', 'order_number', 'status', 'created_at'];
    const missingFields = requiredFields.filter((field) => !order[field]);

    if (missingFields.length > 0) {
      wsLog.warn('⚠️ Order validation failed - missing required fields:', {
        source,
        orderId: order.id,
        missingFields,
      });
      return false;
    }

    // Validar formato do status (apenas em modo debug)
    if (__DEBUG_MODE__) {
      const validStatuses = ['pendente', 'em_preparo', 'pronto', 'entregue', 'cancelado', 'fila', 'preparando', 'pronto'];
      if (!validStatuses.includes(order.status)) {
        wsLog.warn('⚠️ Invalid status found:', {
          source,
          orderId: order.id,
          status: order.status,
        });
      }
    }

    return true;
  }, []);

  // Função para calcular resumo por status (formato simples para UI)
  const calculateOrderSummary = useCallback((orders: any[]) => {
    const summary = orders.reduce(
      (acc, order) => {
        const status = order.status;

        // Ignorar pedidos entregues - eles não devem aparecer em nenhuma contagem
        if (status === 'entregue') {
          return acc;
        }

        // Converter status da API para formato do componente
        const componentStatus = status === 'fila' ? 'fila' : status === 'preparando' ? 'preparando' : status === 'pronto' ? 'pronto' : null; // Outros status não aparecem nas colunas principais

        if (componentStatus) {
          acc[componentStatus] = (acc[componentStatus] || 0) + 1;
        }
        return acc;
      },
      {} as Record<string, number>,
    );

    return {
      fila: summary.fila || 0,
      preparando: summary.preparando || 0,
      pronto: summary.pronto || 0,
    };
  }, []);

  // Memoizar processamento de dados dos pedidos para evitar re-execuções
  //
  // O retorno tem tipo próprio: `summary` aqui é a contagem por coluna da tela,
  // enquanto o `summary` de TodayOrdersResponse é o resumo da API
  // ({total_orders, by_status, total_revenue}). Reaproveitar o tipo da resposta
  // para carregar um agregado de UI misturava dois conceitos.
  const ordersData: FilaOrdersView = useMemo(() => {
    const baseOrders = initialOrdersData?.orders ?? [];
    let data: FilaOrdersView = {
      orders: baseOrders,
      total: baseOrders.length,
      summary: calculateOrderSummary(baseOrders),
    };

    // Se WebSocket estiver conectado e houver atualizações, apenas atualizar status dos pedidos existentes
    if (isConnected && websocketOrders.length > 0 && baseOrders.length > 0) {
      if (__DEBUG_MODE__) {
        wsLog.debug('🔗 Atualizando status via WebSocket:', {
          isConnected,
          websocketOrdersCount: websocketOrders.length,
          initialOrdersCount: baseOrders.length,
        });
      }

      // Atualizar apenas status dos pedidos existentes (nunca substituir)
      const updatedOrders = baseOrders.map((initialOrder: any) => {
        // Procurar atualização correspondente no WebSocket
        const wsUpdate = websocketOrders.find((wsOrder: any) => wsOrder.id === initialOrder.id);

        if (wsUpdate && wsUpdate.status !== initialOrder.status) {
          if (__DEBUG_MODE__) {
            wsLog.debug('🔄 Atualizando status do pedido:', {
              orderId: initialOrder.id,
              orderNumber: initialOrder.order_number,
              oldStatus: initialOrder.status,
              newStatus: wsUpdate.status,
            });
          }

          // Apenas atualizar o status se for diferente, manter todos os outros dados da API
          return {
            ...initialOrder,
            status: wsUpdate.status, // Apenas o status vem do WebSocket
          };
        }

        return initialOrder; // Manter pedido original se não houver atualização
      });

      data = {
        orders: updatedOrders,
        total: updatedOrders.length,
        summary: calculateOrderSummary(updatedOrders),
      };
    }

    return data;
  }, [initialOrdersData, isConnected, websocketOrders, calculateOrderSummary]);

  // Memoizar transformação dos pedidos para o formato esperado
  const orders: Order[] = useMemo(() => {
    const rawOrders = Array.isArray(ordersData) ? ordersData : ordersData?.orders || [];

    wsLog.debug('🔍 Debug - Processing orders:', {
      rawOrdersCount: rawOrders.length,
      ordersDataStructure: Array.isArray(ordersData) ? 'array' : typeof ordersData,
      ordersDataKeys: ordersData && !Array.isArray(ordersData) ? Object.keys(ordersData) : [],
      isConnected,
      websocketOrdersCount: websocketOrders.length,
      rawOrdersSample: rawOrders.slice(0, 2).map((o: any) => ({
        id: o.id,
        order_number: o.order_number,
        status: o.status,
        student_name: o.student_name,
        hasRequiredFields: !!(o.id && o.order_number && o.student_name && o.status && o.created_at),
      })),
      timestamp: new Date().toISOString(),
    });

    const filteredOrders = rawOrders.filter((order: any) => {
      // Primeiro, filtrar pedidos entregues - eles não devem aparecer em nenhuma coluna
      if (order.status === 'entregue') {
        wsLog.debug('🚫 Filtering out delivered order:', {
          orderId: order.id,
          orderNumber: order.order_number,
          status: order.status,
        });
        return false;
      }

      // Validar dados antes de transformar
      const isValid = validateOrderData(order, 'ordersData');
      if (!isValid) {
        wsLog.warn('❌ Order validation failed:', {
          orderId: order.id,
          orderNumber: order.order_number,
          orderData: order,
          missingFields: ['id', 'order_number', 'status', 'created_at'].filter((field) => !order[field]),
          note: 'student_name e user_name não são obrigatórios',
        });
      }
      return isValid;
    });

    wsLog.debug('🔍 Debug - After filtering:', {
      originalCount: rawOrders.length,
      filteredCount: filteredOrders.length,
      filteredOut: rawOrders.length - filteredOrders.length,
      filteredOrders: filteredOrders.map((o: any) => ({
        id: o.id,
        order_number: o.order_number,
        status: o.status,
      })),
    });

    return (
      filteredOrders
        .map((order: any) => {
          // Calcular total amount se não existir
          const totalAmount =
            order.total_amount || order.products?.reduce((sum: number, product: any) => sum + product.item_price * product.quantity, 0) || 0;

          // Obter nome do cliente - tentar student_name, user_name, ou usar padrão
          const customerName = order.student_name || order.user_name || order.customer_name || 'Cliente';
          
          const transformedOrder = {
            id: order.id,
            orderNumber: parseInt(order.order_number) || parseInt(order.id.slice(-6), 16) % 1000,
            customerName: customerName,
            customerGrade: order.class_name || order.student_class || order.class || 'N/A', // Compatibilidade: tentar múltiplos campos
            status:
              order.status === 'fila' || order.status === 'preparando' || order.status === 'pronto'
                ? (order.status as 'fila' | 'preparando' | 'pronto')
                : 'fila', // Default para fila
            createdAt: new Date(order.created_at),
            startedAt: order.updated_at ? new Date(order.updated_at) : undefined,
            completedAt: order.delivered_at ? new Date(order.delivered_at) : undefined,
            items:
              order.products?.map((product: any) => ({
                // Corrigido: products em vez de items
                id: product.product_id,
                product_name: product.product_name,
                quantity: product.quantity,
                item_price: product.item_price,
              })) || [],
            totalAmount,
          };

          // Validar ordem transformada (usando dados originais, não transformados)
          // Não validar novamente com dados transformados pois já validamos antes
          // A validação já foi feita com os dados originais, então podemos confiar

          return transformedOrder;
        })
        .filter((order): order is NonNullable<typeof order> => Boolean(order)) || []
    ); // Type-safe null removal
  }, [ordersData, validateOrderData]);

  // Debug final após transformação
  useEffect(() => {
    wsLog.debug('🎯 Debug - Final transformed orders:', {
      finalOrdersCount: orders.length,
      ordersDataStructure: Array.isArray(ordersData) ? 'array' : typeof ordersData,
      ordersDataHasOrders: !!(ordersData && !Array.isArray(ordersData) && ordersData.orders),
      finalOrders: orders.map((order) => ({
        id: order.id,
        orderNumber: order.orderNumber,
        status: order.status,
        customerName: order.customerName,
        itemsCount: order.items?.length || 0,
      })),
      distribution: {
        fila: orders.filter((o) => o.status === 'fila').length,
        preparando: orders.filter((o) => o.status === 'preparando').length,
        pronto: orders.filter((o) => o.status === 'pronto').length,
      },
      timestamp: new Date().toISOString(),
    });
  }, [orders, ordersData]);

  // Estados combinados para loading e error
  const isLoading = isInitialLoading && websocketOrders.length === 0;
  const error = webSocketError || initialError;

  // Debug logs detalhados (apenas em modo debug)
  if (__DEBUG_MODE__) {
    wsLog.debug('🔍 Debug - Fila Management State:', {
      establishmentId,
      isConnected,
      websocketOrdersCount: websocketOrders.length,
      transformedOrdersCount: orders.length,
      currentInterval: currentInterval
        ? {
            id: currentInterval.id,
            name: currentInterval.name,
          }
        : null,
      config: {
        autoIntervalChange: config.autoIntervalChange,
        orderType: config.orderType,
      },
      intervalManager: {
        enabled: !!establishmentId && config.autoIntervalChange === true,
        isChecking: isCheckingInterval,
        lastChecked,
        intervalError: !!intervalError,
      },
      timestamp: new Date().toISOString(),
    });
  }

  // Debug dos dados brutos - sempre logar para diagnóstico
  useEffect(() => {
    if (initialOrdersData?.orders && initialOrdersData.orders.length > 0) {
      wsLog.debug('📋 Debug - Initial Orders Data RECEBIDO:', {
        count: initialOrdersData.orders.length,
        orders: initialOrdersData.orders.map((order: any) => ({
          id: order.id,
          order_number: order.order_number,
          student_name: order.student_name,
          status: order.status,
          created_at: order.created_at,
          hasProducts: !!order.products,
          productsCount: order.products?.length || 0,
        })),
        rawData: initialOrdersData,
        timestamp: new Date().toISOString(),
      });
    } else if (!isInitialLoading) {
      wsLog.warn('⚠️ Debug - NO INITIAL ORDERS DATA:', {
        initialOrdersData,
        isInitialLoading,
        error: initialError,
        apiParams,
        isConfigValid,
        timestamp: new Date().toISOString(),
      });
    }
  }, [initialOrdersData, isInitialLoading, initialError, apiParams, isConfigValid]);

  // Debug dos dados do WebSocket - apenas quando mudar
  useEffect(() => {
    if (__DEBUG_MODE__ && websocketOrders.length > 0) {
      wsLog.debug('📋 Debug - WebSocket Orders Data:', {
        count: websocketOrders.length,
        timestamp: new Date().toISOString(),
      });
    }
  }, [websocketOrders]);

  // Debug dos dados transformados - apenas quando mudar
  useEffect(() => {
    if (__DEBUG_MODE__ && orders.length > 0) {
      const statusCount = orders.reduce(
        (acc, order) => {
          acc[order.status] = (acc[order.status] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      wsLog.debug('📊 Debug - Final transformed orders:', {
        count: orders.length,
        statusCount,
        timestamp: new Date().toISOString(),
      });
    }
  }, [orders]);

  // Refs para evitar múltiplos toasts do mesmo erro
  const lastOrdersErrorTimeRef = useRef<number>(0);
  const lastConfigErrorTimeRef = useRef<number>(0);

  // Tratamento de erro
  useEffect(() => {
    if (error) {
      const now = Date.now();
      // Só mostrar toast se passou mais de 5 segundos desde o último
      if (now - lastOrdersErrorTimeRef.current > 5000) {
        lastOrdersErrorTimeRef.current = now;
        toast.error('Erro ao carregar pedidos. Tente novamente.');
        wsLog.error('Error loading orders:', error);
      }
    }

    // Erro de configuração
    if (!isConfigValid) {
      const now = Date.now();
      // Só mostrar toast se passou mais de 5 segundos desde o último
      if (now - lastConfigErrorTimeRef.current > 5000) {
        lastConfigErrorTimeRef.current = now;
        wsLog.error('❌ Invalid configuration for orders:', {
          config: {
            orderType: config.orderType,
            autoIntervalChange: config.autoIntervalChange,
            selectedInterval: config.selectedInterval,
            establishmentId: config.establishmentId,
          },
          currentIntervalId: currentInterval?.id,
        });

        toast.error('Configuração inválida. Verifique as configurações da fila.', {
          duration: 5000,
        });
      }
    }
  }, [error, isConfigValid, config, currentInterval]);

  // Tratamento de status de conexão WebSocket com mensagens mais claras
  useEffect(() => {
    if (connectionStatus === 'error') {
      const errorMessage = webSocketError 
        ? `Erro na conexão WebSocket: ${webSocketError.message}`
        : 'Erro na conexão em tempo real. Verifique a configuração e tente reconectar.';
      
      toast.error(errorMessage, {
        duration: 5000,
        action: {
          label: 'Reconectar',
          onClick: () => {
            if (reconnect) {
              reconnect();
            }
          },
        },
      });
    } else if (connectionStatus === 'connected') {
      wsLog.debug('✅ Conectado ao WebSocket - atualizações em tempo real ativas');
    } else if (connectionStatus === 'disconnected' && config.establishmentId) {
      // Apenas mostrar aviso se tiver establishmentId configurado (evitar spam)
      wsLog.warn('⚠️ WebSocket desconectado. Verifique a configuração VITE_WS_URL e o servidor.');
    }
  }, [connectionStatus, webSocketError, reconnect, config.establishmentId]);

  // Memoizar distribuição das colunas para evitar re-filtros
  const { filaOrders, preparandoOrders, prontoOrders } = useMemo(() => {
    const result = {
      filaOrders: orders.filter((order) => order.status === 'fila'),
      preparandoOrders: orders.filter((order) => order.status === 'preparando'),
      prontoOrders: orders.filter((order) => order.status === 'pronto'),
    };

    // Debug log apenas quando os dados mudarem
    if (__DEBUG_MODE__) {
      wsLog.debug('📊 Debug - Final column distribution:', {
        fila: result.filaOrders.length,
        preparando: result.preparandoOrders.length,
        pronto: result.prontoOrders.length,
        total: orders.length,
        timestamp: new Date().toISOString(),
      });
    }

    return result;
  }, [orders]);

  const Column = ({
    title,
    count,
    icon: Icon,
    orders,
    titleSize,
  }: {
    title: string;
    count?: number;
    icon?: any;
    orders: Order[];
    titleSize?: number;
  }) => {
    // Separar pedidos: primeiros 5 expandidos, resto compacto
    const EXPANDED_LIMIT = 5;
    const expandedOrders = orders.slice(0, EXPANDED_LIMIT);
    const compactOrders = orders.slice(EXPANDED_LIMIT);
    const hasCompactMode = compactOrders.length > 0;

    return (
      <div className="flex min-h-0 flex-1 flex-col border-2 border-brand-primary-300 last:border-r-2">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-gray-400 bg-brand-primary-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {Icon && (
                <div className="rounded-lg bg-brand-primary p-2 shadow-sm">
                  <Icon className="h-8 w-8 text-black" />
                </div>
              )}
              <div>
                <h2 className="font-medium text-black" style={{ fontSize: `${titleSize}rem` }}>
                  {title}
                </h2>
                {/* <p className="text-sm font-semibold text-black">
                  {count} {count === 1 ? 'pedido' : 'pedidos'}
                </p> */}
              </div>
            </div>
            <div className="rounded-lg bg-brand-primary-200 px-3 py-1 text-5xl font-semibold text-black shadow-sm">{count}</div>
          </div>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-hidden bg-gray-50">
          {orders.length === 0 ? (
            <div className="flex h-full items-center justify-center py-12 text-center">
              <div>
                <Icon className="mx-auto mb-4 h-16 w-16 text-gray-300" />
                <p className="text-lg font-medium text-gray-400">Nenhum pedido</p>
                <p className="text-sm text-gray-400">Aguardando novos pedidos...</p>
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-col overflow-hidden">
              {/* Pedidos Expandidos (primeiros 5) */}
              {expandedOrders.length > 0 && (
                <div className="flex-shrink-0 space-y-3 overflow-y-auto p-4">
                  {expandedOrders.map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      expanded={expandedCards}
                      orderNumberSize={config.orderNumberTextSize}
                    />
                  ))}
                </div>
              )}

              {/* Pedidos Compactos (do 6º em diante) */}
              {hasCompactMode && (
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <div className="bg-gray-50">
                    {/* Grid compacto dinâmico - sempre 2 linhas, colunas aumentam */}
                    <CompactOrderGrid orders={compactOrders} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-brand-primary"></div>
          <p className="text-lg text-gray-600">Carregando pedidos...</p>
          {/* O bloco "Dados recebidos: N pedidos" saiu daqui: dentro do estado
              de carregamento o React Query garante data === undefined, então
              ele nunca era exibido. */}
        </div>
      </div>
    );
  }

  // Banner de diagnóstico: dados chegaram da API mas nada foi exibido.
  //
  // Só aparece em modo debug — o texto manda "verificar o console", então é
  // ferramenta de desenvolvimento e não algo para o atendente ver no meio do
  // recreio.
  //
  // A contagem desconta os pedidos entregues: quando o WebSocket marca um
  // pedido como entregue, a tela o esconde de propósito, e comparar com a
  // lista crua da API acusaria anomalia onde o comportamento está certo.
  const displayableOrders = (Array.isArray(ordersData) ? ordersData : (ordersData?.orders ?? [])).filter(
    (order: any) => order?.status !== 'entregue',
  );
  const hasDataButNoDisplay = __DEBUG_MODE__ && displayableOrders.length > 0 && orders.length === 0;

  return (
    <div className="flex h-screen w-full flex-col bg-gray-100">
      {/* Debug Banner - Mostrar se houver dados mas não exibindo */}
      {hasDataButNoDisplay && (
        <div className="flex-shrink-0 border-b-2 border-yellow-400 bg-yellow-50 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm font-semibold text-yellow-800">
                  ⚠️ Dados recebidos mas não exibidos
                </p>
                <p className="text-xs text-yellow-700">
                  API retornou {initialOrdersData?.orders?.length || 0} pedidos, mas {orders.length} foram exibidos.
                  Verifique o console para detalhes.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                wsLog.debug('🔍 Debug completo dos dados:', {
                  initialOrdersData,
                  ordersData,
                  orders,
                  rawOrders: Array.isArray(ordersData) ? ordersData : ordersData?.orders || [],
                  validation: initialOrdersData?.orders?.map((o: any) => ({
                    id: o.id,
                    order_number: o.order_number,
                    student_name: o.student_name,
                    status: o.status,
                    created_at: o.created_at,
                    isValid: validateOrderData(o, 'debug'),
                  })),
                });
              }}
              className="rounded bg-yellow-200 px-3 py-1 text-xs font-medium text-yellow-800 hover:bg-yellow-300"
            >
              Ver Logs
            </button>
          </div>
        </div>
      )}

      {/* Connection Status Indicator */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-lg font-semibold text-gray-800">Fila Digital</h1>

            {/* Interval Information */}
            <div className="flex items-center space-x-2">
              {/* <Calendar className="h-4 w-4 text-blue-500" /> */}
              {currentIntervalName &&
              currentIntervalName !== 'Intervalo não configurado' &&
              currentIntervalName !== 'Erro na configuração' &&
              currentIntervalName !== 'Erro ao carregar configuração' ? (
                <div className="flex items-center space-x-2">
                  <Badge variant="default" className="border-green-200 bg-green-100 text-green-800">
                    {currentIntervalName}
                  </Badge>
                  {lastApiSuccess && (
                    <Badge variant="ghost" className="border-green-200 bg-green-50 text-xs text-green-600">
                      ✓ API
                      <span className="ml-1 text-xs text-gray-400" title={`API sucesso: ${new Date(lastApiSuccess).toLocaleString()}`}>
                        {new Date(lastApiSuccess).toLocaleTimeString()}
                      </span>
                    </Badge>
                  )}
                  {intervalError && (
                    <Badge variant="ghost" className="border-red-200 bg-red-50 text-xs text-red-600">
                      ⚠️ Erro
                      <span className="ml-1 text-xs text-red-500" title="Clique para tentar novamente">
                        <button onClick={forceIntervalCheck} className="underline hover:text-red-700">
                          Tentar
                        </button>
                      </span>
                    </Badge>
                  )}
                  {config.autoIntervalChange && !lastChecked && !isCheckingInterval && (
                    <span className="text-xs text-orange-500" title="Aguardando primeira verificação automática">
                      Iniciando...
                    </span>
                  )}
                </div>
              ) : intervalError ? (
                <div className="flex items-center space-x-2">
                  <Badge variant="default" className="border-red-200 bg-red-100 text-red-800">
                    Erro ao carregar
                  </Badge>
                  <button
                    onClick={forceIntervalCheck}
                    className="flex items-center space-x-1 text-xs text-red-600 hover:text-red-800"
                    title="Tentar verificar intervalo novamente">
                    <RefreshCw className="h-3 w-3" />
                    <span>Tentar novamente</span>
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Badge variant="default" className="border-gray-200 bg-gray-100 text-gray-500">
                    {currentIntervalName || 'Sem intervalo'}
                  </Badge>
                </div>
              )}
            </div>

            {/* WebSocket Status */}
            <div className="flex items-center space-x-2">
              {isConnected ? (
                <>
                  <Wifi className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">Conectado</span>
                  {lastUpdated && <span className="text-xs text-gray-500">Última atualização: {new Date(lastUpdated).toLocaleTimeString()}</span>}
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4 text-red-500" />
                  <span className="text-sm text-red-600">{connectionStatus === 'connecting' ? 'Conectando...' : 'Desconectado'}</span>
                  {connectionStatus === 'error' && (
                    <>
                      <button onClick={reconnect} className="text-xs text-blue-600 underline hover:text-blue-800">
                        Reconectar
                      </button>
                      {webSocketError && (
                        <span className="text-xs text-red-500" title={webSocketError.message}>
                          ⚠️ Erro
                        </span>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-6">
            {/* Manual Interval Check */}
            {/* <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <RefreshCw className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-gray-600">Verificar Intervalo</span>
              </div>
              <button
                onClick={forceIntervalCheck}
                disabled={isCheckingInterval}
                className="rounded border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50">
                {isCheckingInterval ? 'Verificando...' : 'Verificar Agora'}
              </button>
            </div> */}

            {/* Text Size Controls */}
            {/* minSize/maxSize/step foram removidos: TextSizeControl trabalha em
                níveis (minLevel/maxLevel) desde a refatoração e já ignorava
                essas props — os limites em rem nunca chegaram a ser aplicados. */}
            <div className="flex items-center space-x-4 border-l border-gray-300 pl-6">
              <TextSizeControl
                label="Nº Pedido"
                size={config.orderNumberTextSize}
                onSizeChange={setOrderNumberTextSize}
                icon={<Hash className="h-4 w-4 text-black" />}
              />

              <TextSizeControl
                label="Título Fila"
                size={config.queueTitleTextSize}
                onSizeChange={setQueueTitleTextSize}
                icon={<ListTodo className="h-4 w-4 text-black" />}
              />
            </div>

            {/* Expanded Cards Toggle */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <ListTodo className="h-4 w-4 text-black" />
                <span className="text-sm text-gray-600">{expandedCards ? 'Cards Expandidos' : 'Cards Compactos'}</span>
              </div>
              <ToggleSwitch checked={expandedCards} onCheckedChange={setExpandedCards} />
            </div>

          </div>
        </div>
      </div>

      {/* Main Content - 3 Columns */}
      <div className="flex min-h-0 flex-1">
        {config.visibleColumns.fila && (
          <Column title="Fila" count={filaOrders.length} icon={ListTodo} orders={filaOrders} titleSize={config.queueTitleTextSize} />
        )}

        {config.visibleColumns.preparando && (
          <Column title="Preparando" count={preparandoOrders.length} icon={Clock} orders={preparandoOrders} titleSize={config.queueTitleTextSize} />
        )}

        {config.visibleColumns.pronto && (
          <Column title="Pronto" count={prontoOrders.length} icon={CheckCircle} orders={prontoOrders} titleSize={config.queueTitleTextSize} />
        )}
      </div>
    </div>
  );
}
